import { $command, $useKeymap } from '@milkdown/utils';
import { commandsCtx } from '@milkdown/core';
import { liftListItemCommand, listItemSchema } from '@milkdown/preset-commonmark';
import { TextSelection } from '@milkdown/prose/state';
import { listItemBlockComponent, listItemBlockConfig } from '@milkdown/components/list-item-block';
import { listConfig } from '../config';
/**
 * Deletes an empty list item and merges it with the previous item.
 * If this is the first item in the list, lifts it out of the list instead.
 * Handles nested lists by promoting them to the parent list level.
 *
 * @param ctx - editor Ctx
 * @returns a ProseMirror Command
 */
function deleteListItem(ctx) {
    return (state, dispatch) => {
        const { selection } = state;
        // Only works with text selections (not node selections)
        if (!(selection instanceof TextSelection))
            return false;
        const { empty, $from } = selection;
        // Only trigger when cursor is at the start of a line with no selection
        // (empty = no text selected, parentOffset = 0 means cursor at line start)
        if (!empty || $from.parentOffset !== 0)
            return false;
        const listItem = $from.node(-1); // The list item
        // Verify we're actually inside a list item, not some other block
        if (listItem.type !== listItemSchema.type(ctx))
            return false;
        // Can only delete if the first paragraph is empty
        // (prevents accidental data loss when deleting non-empty list items)
        const paragraph = listItem.firstChild;
        if (!paragraph || paragraph.textContent.length > 0)
            return false;
        const indexInParent = $from.index(-2);
        // Checks if this list item is the first child in its parent list (index 0 means first position)
        if (indexInParent === 0) {
            const commands = ctx.get(commandsCtx);
            // default action: removes the list item from the list structure and promotes its content to the parent level
            return commands.call(liftListItemCommand.key);
        }
        const parentList = $from.node(-2);
        const prevListItem = parentList.child(indexInParent - 1);
        // Verify the previous sibling is a list item (not a different node type)
        // (prevents merging with incompatible node types)
        if (prevListItem.type !== listItemSchema.type(ctx)) {
            return false;
        }
        const currentListItemPos = $from.before(-1);
        const parentListPos = $from.after(-2);
        // dispatch is falsey when we're checking if the editor is in a read-only state
        // or if dispatch is not available. In those cases, this would be a no-op.
        // We're using dispatch here to make the series of actions a singular transaction in Editor events
        if (dispatch) {
            let tr = state.tr;
            // Extract nested content of list item if it exists, lift it to the parent list level
            // Example: In a list with nested items:
            //   - Item 1
            //     - Nested Item (cursor here, empty paragraph)
            //   - Item 2
            //
            // currentListItemPos = start of "Nested Item" node
            // parentListPos = end of parent list (after Item 1's closing tag)
            // This positions nested content correctly when lifted
            if (listItem.childCount > 1) {
                const nestedStart = currentListItemPos + paragraph.nodeSize + 1;
                const nestedEnd = currentListItemPos + listItem.nodeSize - 1;
                const nestedSlice = state.doc.slice(nestedStart, nestedEnd);
                tr = tr.insert(parentListPos, nestedSlice.content);
            }
            // Now that we know there are no children, delete the list item
            tr = tr.delete(currentListItemPos, currentListItemPos + listItem.nodeSize);
            // Calculate position at start of previous list item in the new doc
            // (after we've deleted the current item, positions shift)
            const prevItemPos = currentListItemPos - prevListItem.nodeSize;
            // Navigate to end of first child (paragraph) to place cursor after merge
            const endOfPrevParagraph = prevItemPos + prevListItem.firstChild.nodeSize;
            tr = tr.setSelection(TextSelection.create(tr.doc, endOfPrevParagraph));
            dispatch(tr);
        }
        return true;
    };
}
const deleteListItemCommand = $command('DeleteListItem', ctx => () => deleteListItem(ctx));
const listItemKeyMapConfig = $useKeymap('listItemKeymap', {
    FastDeleteListItem: {
        shortcuts: ['Delete', 'Backspace'],
        priority: 75,
        command: ctx => {
            const commands = ctx.get(commandsCtx);
            return () => commands.call(deleteListItemCommand.key);
        },
    },
});
const additionalListCommands = [deleteListItemCommand];
const listConfigPlugin = (ctx) => {
    ctx.set(listItemBlockConfig.key, listConfig);
    return;
};
// flattened to ensure all plugins are in the root array
export const listPlugin = [
    listItemBlockComponent,
    additionalListCommands,
    listItemKeyMapConfig,
    listConfigPlugin,
].flat();
//# sourceMappingURL=listPlugin.js.map